import styled, { css } from 'styled-components';
import { Disableable, getSpacingToken, getToken, Size } from '@optimus-web/common';
import { IconName } from '@optimus-web/icons';
import { Icon } from '../../../Icon';
import { getContainerPadding } from '../../../Input/getInputStyles';
import { Stack } from '../../../Stack';
import { TextInput } from '../../../TextInput';
import { Typography } from '../../../typography';
import { ComboInputElementProps } from '../../types';
import { getValueContainerHeight, getValueContainerVerticalPadding } from './getSelectStyles';

// ============================================================================
// Main Input Components
// ============================================================================

/**
 * Main combo input container that handles the overall layout and interactions
 */
export const ComboInputElement = styled.div<ComboInputElementProps>`
    display: inline-flex;
    flex-direction: row;
    align-items: center;
    cursor: ${(props) => {
        if (props.disabled) return 'not-allowed';
        if (props.readOnly) return 'default';
        return 'pointer';
    }};
    width: 100%;
    outline: none;
    ${({ hasTags, size }) =>
        hasTags
            ? css`
                  min-height: calc(${getValueContainerHeight({ size })}px - ${getToken('borderWidth100')}px * 2);
              `
            : css`
                  height: calc(${getValueContainerHeight({ size })}px - ${getToken('borderWidth100')}px * 2);
              `}
`;

/**
 * Container for the selected values/tags
 */
interface ValueContainerElementProps {
    hasRightDetail?: boolean;
    hasTags?: boolean;
    size?: Size;
    compact?: boolean;
}

export const ValueContainerElement = styled.div<ValueContainerElementProps>`
    flex-grow: 1;
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    position: relative;
    ${({ size, hasTags }) => getValueContainerVerticalPadding(size, hasTags)};
    padding-left: ${({ size }) => getContainerPadding({ inputSize: size })}px;
    padding-right: ${({ hasRightDetail, size }) =>
        hasRightDetail ? getSpacingToken('50') : getContainerPadding({ inputSize: size })}px;
    box-sizing: border-box;
    min-width: 0;

    & > * {
        ${({ hasTags }) =>
            hasTags &&
            css`
                margin-top: 1px;
                margin-bottom: 1px;
            `}

        ${({ hasTags, compact }) =>
            hasTags &&
            compact &&
            css`
                margin-top: ${getSpacingToken('200')}px;
            `}

        &:not(:last-child) {
            margin-right: ${getSpacingToken('50')}px;
        }

        &:first-child:empty + :last-child,
        &:only-child {
            ${({ compact, hasTags }) =>
                !(compact && hasTags) &&
                css`
                    width: 100%;
                `};
        }
    }

    ${(props) =>
        props.compact &&
        css`
            height: calc(${getValueContainerHeight}px - ${getToken('borderWidth100')}px * 2);
            overflow-y: hidden;
        `}
`;

// ============================================================================
// Detail Containers (Left/Right sections)
// ============================================================================

const DetailContainer = styled(Stack).attrs({ alignment: 'end', itemAlignment: 'center' })``;

export const LeftDetailContainer = styled(DetailContainer)`
    height: 100%;
    color: ${getToken('textStaticSecondary')};
    border-top-left-radius: ${getToken('borderRadius50')}px;
    border-bottom-left-radius: ${getToken('borderRadius50')}px;
    padding: 0 0 0 ${getSpacingToken('100')}px;
`;

export const RightDetailContainer = styled(DetailContainer)`
    height: 100%;
    border-top-right-radius: ${getToken('borderRadius50')}px;
    border-bottom-right-radius: ${getToken('borderRadius50')}px;
    padding-right: ${getSpacingToken('100')}px;
`;

export const RightVariantContainer = styled.div`
    display: inline-flex;
    margin-right: ${getSpacingToken('100')}px;
`;

// ============================================================================
// Icons and Indicators
// ============================================================================

export const ClearIcon = styled(Icon).attrs({
    value: IconName.CrossClose,
})`
    align-self: center;
    cursor: pointer;
    outline: none;
`;

export const SpinnerContainer = styled.div`
    display: inline-flex;
`;

export const RestTagCounterContainer = styled(DetailContainer)`
    height: 100%;
    padding-right: ${getSpacingToken('100')}px;
    min-width: ${getToken('sizing400')}px;
`;

// ============================================================================
// Single Value Display
// ============================================================================

export interface SingleValueElementProps extends Disableable {
    focused?: boolean;
    size?: Size;
}

export const SingleValueElement = styled(Typography)<SingleValueElementProps>`
    max-width: calc(100% - ${({ theme }) => getSpacingToken('200')({ theme })}px);
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
`;

// ============================================================================
// Search and Drawer Components
// ============================================================================

export const SearchContainerElement = styled.div`
    padding: ${getSpacingToken('100')}px ${getSpacingToken('200')}px ${getSpacingToken('100')}px 0;
`;

export const DrawerInput = styled(TextInput)`
    position: sticky;
    top: 0;
    z-index: 1;
    background-color: ${getToken('backgroundStaticFlat')};
`;

export const MobileContentStack = styled(Stack)`
    width: 100%;
`;
